import type { ChangeEvent } from "react";

import { isUndefined } from "lodash";

import type { Asset } from "~/v2/api/rest";
import type { Account, Position, Product } from "~/v2/api/rest/account";
import type { Quote } from "~/v2/api/rest/data";
import type { CreateOrderRequest } from "~/v2/api/rest/order";
import type {
  CryptoOrderType,
  CryptoQuantityType,
  CryptoTimeInForce,
  Side,
} from "~/v2/constants/cryptoOrder";

export const isGTZero = (number: number | undefined) =>
  !isUndefined(number) && number > 0;

export const findPosition = (positions: Position[], symbol: string) =>
  positions.find((position) => position.symbol === symbol);

const DEFAULT_PRECISION = 8;

export interface CreateCryptoOrderObjectParams {
  symbol: string;
  side: Side;
  orderType: CryptoOrderType;
  timeInForce: CryptoTimeInForce;
  quantity: number;
  quantityType: CryptoQuantityType;
  limitPrice?: number;
  stopPrice?: number;
}

export const createCryptoOrderObject = ({
  symbol,
  side,
  orderType,
  timeInForce,
  quantity,
  quantityType,
  limitPrice,
  stopPrice,
}: CreateCryptoOrderObjectParams): CreateOrderRequest => {
  const isNotional = quantityType === "quote" && orderType === "market";

  const orderRequest: CreateOrderRequest = {
    symbol,
    side,
    type: orderType,
    time_in_force: timeInForce,
  };

  if (isNotional) {
    orderRequest.notional = quantity.toString();
  } else {
    orderRequest.qty = quantity.toString();
  }

  if (orderType === "limit" || orderType === "stop_limit") {
    orderRequest.limit_price = limitPrice?.toString();
  }

  if (orderType === "stop_limit") {
    orderRequest.stop_price = stopPrice?.toString();
  }

  return orderRequest;
};

export interface GetIsSubmitDisabledParams {
  account: Account | undefined;
  asset: Asset | undefined;
  quote: Quote | undefined;
  product: Product | undefined;
  quantity: number;
  orderType: CryptoOrderType;
  limitPrice: number | undefined;
  stopPrice: number | undefined;
}

export const getIsSubmitDisabled = ({
  account,
  asset,
  quote,
  product,
  quantity,
  orderType,
  limitPrice,
  stopPrice,
}: GetIsSubmitDisabledParams): boolean => {
  if (!account || !asset || !quote || !product) {
    return true;
  }

  if (!isGTZero(quantity)) {
    return true;
  }

  if (
    (orderType === "limit" || orderType === "stop_limit") &&
    !isGTZero(limitPrice)
  ) {
    return true;
  }

  if (orderType === "stop_limit" && !isGTZero(stopPrice)) {
    return true;
  }

  return false;
};

/**
 * Alpaca position symbols use the format {asset}USD (e.g. BTCUSD, USDTUSD).
 * This converts a currency name to the position symbol format.
 */
export const toPositionSymbol = (currency: string): string =>
  currency.endsWith("USD") ? currency : `${currency}USD`;

export const parseCryptoPair = (
  symbol: string,
): { baseCurrency: string; quoteCurrency: string } => {
  const split = symbol.split("/");
  return {
    baseCurrency: split.at(0) ?? symbol,
    quoteCurrency: split.at(1) ?? "",
  };
};

export const getQuotePrecision = (lastPrice: number | undefined): number => {
  if (lastPrice && lastPrice > 0 && lastPrice < 0.01) {
    return Math.min(
      1 - Math.floor(Math.log(lastPrice) / Math.log(10)),
      DEFAULT_PRECISION,
    );
  }
  return DEFAULT_PRECISION;
};

export const CRYPTO_LOCALE_OPTIONS: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: DEFAULT_PRECISION,
};

/**
 * Count the number of decimal places in an increment string.
 * e.g. "0.0001" → 4, "1" → 0, "0.1" → 1
 */
export const getDecimalPlaces = (
  incrementStr: string | undefined,
): number | undefined => {
  if (!incrementStr) {
    return undefined;
  }
  const parsedNumber = parseFloat(incrementStr);
  if (!Number.isFinite(parsedNumber) || parsedNumber <= 0) {
    return undefined;
  }
  const dotIndex = incrementStr.indexOf(".");
  if (dotIndex === -1) {
    return 0;
  }
  return incrementStr.length - dotIndex - 1;
};

/**
 * Clamp a numeric string to at most `maxDecimals` decimal places by truncating
 * (not rounding). Returns the original string when no clamping is needed.
 */
export const clampDecimals = (
  raw: string,
  maxDecimals: number | undefined,
): string => {
  if (maxDecimals === undefined) {
    return raw;
  }
  const dotIndex = raw.indexOf(".");
  if (dotIndex === -1) {
    return raw;
  }
  const allowed = dotIndex + 1 + maxDecimals;
  return raw.length > allowed ? raw.slice(0, allowed) : raw;
};

/**
 * Creates an onChange handler that clamps decimal places before forwarding
 * to the form field's onChange. Shared by QuantityInput and PriceInput.
 */
export const createClampedChangeHandler =
  (
    maxDecimals: number | undefined,
    fieldOnChange: (...event: unknown[]) => void,
  ) =>
  (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const clamped = clampDecimals(raw, maxDecimals);
    fieldOnChange(clamped !== raw ? clamped : event);
  };
